import React, { useRef, useState } from 'react';
import { Formik, Form, Field } from 'formik';
import ReCAPTCHA from 'react-google-recaptcha';
import validator from 'validator';
import { useViewlogies } from 'context';
import { insertGuest, setGuestCookie, getGuestVariantCookie, setViewerContactCookie } from 'services/guest';
import config from 'config/default';

const Step2 = ({ onComplete, color }) => {
    const {
        event: { id, organizationId },
    } = useViewlogies();

    const recaptchaRef = useRef();
    const variant = getGuestVariantCookie();
    const [isRecaptchaError, setIsRecaptchaError] = useState(false);
    const [submitError, setSubmitError] = useState('');

    return (
        <>
            <h2 className="registration-title">Show that you attended</h2>
            <p className="registration-subtitle">Register for the event</p>

            <Formik
                initialValues={{ name: '', email: '', preplanningInterest: false }}
                onSubmit={async values => {
                    setSubmitError('');
                    const token = recaptchaRef.current?.getValue();
                    if (!token) {
                        setIsRecaptchaError(true);
                        return;
                    }
                    setIsRecaptchaError(false);
                    const { status } = await insertGuest(id, organizationId, { ...values, abVariant: variant }, token);
                    if (status === 201) {
                        setGuestCookie(id);
                        setViewerContactCookie({ name: values.name, email: values.email });
                    } else {
                        setSubmitError('There was an error signing the guest list.');
                    }
                    onComplete();
                }}
            >
                {({ values, touched, isSubmitting }) => {
                    const isInvalidName = touched.name && !values.name;
                    const isInvalidEmail = touched.email && !validator.isEmail(values.email);
                    const isSubmitDisabled =
                        !values.name || !values.email || isInvalidName || isInvalidEmail || isSubmitting;

                    return (
                        <Form className="registration-form">
                            <span className="registration-input-label">First and Last Name</span>
                            <Field
                                name="name"
                                className={`registration-input${isInvalidName ? ' invalid' : ''}`}
                                maxLength={36}
                            />

                            <span className="registration-input-label">Email</span>
                            <Field
                                name="email"
                                type="email"
                                className={`registration-input${isInvalidEmail ? ' invalid' : ''}`}
                            />

                            <div className="registration-checkbox-row">
                                <Field name="preplanningInterest" type="checkbox" id="preplanning" />
                                <label htmlFor="preplanning">Please also email me a funeral planning guide PDF</label>
                            </div>

                            <div
                                className="registration-recaptcha-container"
                                style={{ display: isSubmitDisabled ? 'none' : 'block' }}
                            >
                                <ReCAPTCHA
                                    ref={recaptchaRef}
                                    sitekey={config.recaptchaSiteKey}
                                    onChange={() => {
                                        setIsRecaptchaError(false);
                                        setSubmitError('');
                                    }}
                                />
                                {isRecaptchaError && (
                                    <div className="registration-recaptcha-error">
                                        Please verify that you are not a robot.
                                    </div>
                                )}
                            </div>

                            {submitError && <div className="registration-error">{submitError}</div>}

                            <button
                                type="submit"
                                className="registration-primary-btn"
                                style={{ backgroundColor: color }}
                                disabled={isSubmitDisabled}
                            >
                                Sign Guest List
                            </button>

                            <p className="registration-disclaimer">
                                By signing the guest list, you agree to Viewlogies{' '}
                                <a href="https://www.viewlogies.com/terms" target="_blank" rel="noreferrer">
                                    Terms of Service
                                </a>{' '}
                                and{' '}
                                <a href="https://www.viewlogies.com/privacy" target="_blank" rel="noreferrer">
                                    Privacy Policy
                                </a>
                            </p>

                            <button
                                type="button"
                                className="registration-skip-btn"
                                onClick={onComplete}
                                disabled={isSubmitting}
                            >
                                Or Continue Without Signing
                            </button>
                        </Form>
                    );
                }}
            </Formik>
        </>
    );
};

export default Step2;
